import React, { useState } from 'react';
import { relatorioService } from '../services';
import jsPDF from 'jspdf';
import { 
  Calendar,
  TrendingUp,
  Users,
  Package,
  BarChart3,
  Download,
  Search,
  AlertCircle,
  Clock
} from 'lucide-react';

const Relatorios = () => {
  const [loading, setLoading] = useState(false);
  const [loadingPrevisao, setLoadingPrevisao] = useState(false);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: ''
  });
  const [relatorio, setRelatorio] = useState(null);
  const [previsao, setPrevisao] = useState(null);
  const [error, setError] = useState('');
  const [errorPrevisao, setErrorPrevisao] = useState('');

  // Estilos centralizados
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '30px',
    color: '#333'
  };

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0'
  };

  const buttonPrimaryStyle = {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.3s'
  };

  const buttonSuccessStyle = {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.3s'
  };

  const formGroupStyle = {
    marginBottom: '15px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#555'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '15px'
  };

  const thStyle = {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #dee2e6',
    fontWeight: 'bold',
    color: '#495057'
  };

  const tdStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #dee2e6'
  };

  const errorStyle = {
    padding: '12px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    color: '#721c24',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px'
  };

  // Definir datas padrão (últimos 30 dias)
  React.useEffect(() => {
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    
    setFiltros({
      dataInicio: trintaDiasAtras.toISOString().split('T')[0],
      dataFim: hoje.toISOString().split('T')[0]
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const gerarRelatorio = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Por favor, selecione as datas de início e fim.');
      return;
    }

    if (new Date(filtros.dataInicio) > new Date(filtros.dataFim)) {
      setError('A data de início deve ser anterior à data de fim.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const data = await relatorioService.getConsumoMedicamentos(
        filtros.dataInicio,
        filtros.dataFim
      );
      setRelatorio(data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      setError('Erro ao gerar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const gerarPrevisao = async () => {
    setLoadingPrevisao(true);
    setErrorPrevisao('');
    
    try {
      const data = await relatorioService.getPrevisaoMedicamentos();
      setPrevisao(data);
    } catch (error) {
      console.error('Erro ao gerar previsão:', error);
      setErrorPrevisao('Erro ao gerar previsão. Tente novamente.');
    } finally {
      setLoadingPrevisao(false);
    }
  };

  const exportarRelatorio = () => {
    if (!relatorio && !previsao) return;
    
    const doc = new jsPDF();
    let yPosition = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;
    
    // Função para adicionar nova página se necessário
    const checkNewPage = (additionalHeight = 20) => {
      if (yPosition + additionalHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
    };
    
    // Cabeçalho do documento
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('SISTEMA DE FARMÁCIA POPULAR', margin, yPosition);
    yPosition += 10;
    
    doc.setFontSize(14);
    doc.text('Relatórios de Medicamentos', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition);
    yPosition += 15;
    
    if (relatorio) {
      checkNewPage(40);
      
      // Seção de Relatório de Consumo
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('RELATÓRIO DE CONSUMO', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Período: ${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`, margin, yPosition);
      yPosition += 15;
      
      // Resumo
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMO', margin, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`• Total de Medicamentos Consumidos: ${relatorio.resumo.totalMedicamentosConsumidos}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`• Total de Pacientes Atendidos: ${relatorio.resumo.totalPacientesAtendidos}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`• Total de Medicamentos Diferentes: ${relatorio.resumo.totalMedicamentosDiferentes}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`• Total de Liberações: ${relatorio.resumo.totalLiberacoes}`, margin, yPosition);
      yPosition += 15;
      
      // Tabela de consumo por medicamento
      if (relatorio.consumoPorMedicamento.length > 0) {
        checkNewPage(50);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('CONSUMO POR MEDICAMENTO', margin, yPosition);
        yPosition += 10;
        
        // Cabeçalho da tabela
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Medicamento', margin, yPosition);
        doc.text('Categoria', margin + 60, yPosition);
        doc.text('Consumido', margin + 110, yPosition);
        doc.text('Pacientes', margin + 140, yPosition);
        doc.text('Previsão', margin + 170, yPosition);
        yPosition += 8;
        
        // Linha separadora
        doc.line(margin, yPosition - 2, 190, yPosition - 2);
        
        doc.setFont('helvetica', 'normal');
        relatorio.consumoPorMedicamento.slice(0, 15).forEach(med => {
          checkNewPage(8);
          doc.text(med.nome.substring(0, 25), margin, yPosition);
          doc.text(med.categoria.substring(0, 20), margin + 60, yPosition);
          doc.text(med.totalConsumido.toString(), margin + 110, yPosition);
          doc.text(med.totalPacientes.toString(), margin + 140, yPosition);
          doc.text(med.previsaoProximoMes.toString(), margin + 170, yPosition);
          yPosition += lineHeight;
        });
        yPosition += 10;
      }
    }
    
    if (previsao) {
      checkNewPage(40);
      
      // Seção de Previsão
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PREVISÃO PRÓXIMO MÊS', margin, yPosition);
      yPosition += 15;
      
      // Resumo da previsão
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMO DA PREVISÃO', margin, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`• Total de Medicamentos: ${previsao.resumo.totalMedicamentos}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`• Pacientes Ativos: ${previsao.resumo.totalPacientesAtivos}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`• Quantidade Total Prevista: ${previsao.resumo.quantidadeTotalPrevista} unidades`, margin, yPosition);
      yPosition += lineHeight;
      
      if (previsao.resumo.medicamentoMaiorConsumo) {
        doc.text(`• Maior Consumo: ${previsao.resumo.medicamentoMaiorConsumo.nome} (${previsao.resumo.medicamentoMaiorConsumo.quantidade} unidades)`, margin, yPosition);
        yPosition += lineHeight;
      }
      yPosition += 10;
      
      // Top 10 medicamentos
      if (previsao.top10Medicamentos.length > 0) {
        checkNewPage(50);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('TOP 10 MEDICAMENTOS', margin, yPosition);
        yPosition += 10;
        
        // Cabeçalho da tabela
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Medicamento', margin, yPosition);
        doc.text('Categoria', margin + 60, yPosition);
        doc.text('Tipo Controle', margin + 110, yPosition);
        doc.text('Quantidade', margin + 150, yPosition);
        doc.text('Pacientes', margin + 180, yPosition);
        yPosition += 8;
        
        // Linha separadora
        doc.line(margin, yPosition - 2, 190, yPosition - 2);
        
        doc.setFont('helvetica', 'normal');
        previsao.top10Medicamentos.forEach(med => {
          checkNewPage(8);
          doc.text(med.nome.substring(0, 25), margin, yPosition);
          doc.text(med.categoria.substring(0, 20), margin + 60, yPosition);
          doc.text(med.tipoControle.substring(0, 15), margin + 110, yPosition);
          doc.text(med.quantidadePrevista.toString(), margin + 150, yPosition);
          doc.text(med.totalPacientes.toString(), margin + 180, yPosition);
          yPosition += lineHeight;
        });
        yPosition += 10;
      }
      
      // Previsão por categoria
      if (previsao.categorias.length > 0) {
        checkNewPage(50);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('PREVISÃO POR CATEGORIA', margin, yPosition);
        yPosition += 10;
        
        // Cabeçalho da tabela
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Categoria', margin, yPosition);
        doc.text('Quantidade Total', margin + 80, yPosition);
        doc.text('Nº Medicamentos', margin + 130, yPosition);
        doc.text('Média', margin + 170, yPosition);
        yPosition += 8;
        
        // Linha separadora
        doc.line(margin, yPosition - 2, 190, yPosition - 2);
        
        doc.setFont('helvetica', 'normal');
        previsao.categorias.slice(0, 10).forEach(cat => {
          checkNewPage(8);
          const media = cat.medicamentos > 0 ? Math.round(cat.quantidade / cat.medicamentos) : 0;
          doc.text(cat.categoria.substring(0, 30), margin, yPosition);
          doc.text(cat.quantidade.toString(), margin + 80, yPosition);
          doc.text(cat.medicamentos.toString(), margin + 130, yPosition);
          doc.text(media.toString(), margin + 170, yPosition);
          yPosition += lineHeight;
        });
      }
    }
    
    // Rodapé em todas as páginas
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Página ${i} de ${totalPages}`, margin, pageHeight - 10);
      doc.text('Sistema de Farmácia Popular de Minas Gerais', 190 - 80, pageHeight - 10);
    }
    
    // Nome do arquivo baseado no tipo de relatório
    let fileName = 'relatorio_medicamentos';
    if (relatorio && previsao) {
      fileName += '_completo';
    } else if (relatorio) {
      fileName += '_consumo';
    } else if (previsao) {
      fileName += '_previsao';
    }
    fileName += `_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Salvar o PDF
    doc.save(fileName);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#2c3e50' }}>
          <BarChart3 size={32} />
          Relatórios de Medicamentos
        </h1>
        <p style={{ color: '#6c757d', fontSize: '16px', margin: '10px 0 0 0' }}>
          Análise de consumo e previsão de necessidades
        </p>
      </div>

      {/* Filtros */}
      <div style={cardStyle}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#495057', marginBottom: '20px' }}>
          <Calendar size={20} />
          Período de Análise
        </h3>
        
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Data Início:</label>
            <input
              type="date"
              name="dataInicio"
              value={filtros.dataInicio}
              onChange={handleInputChange}
              style={{
                ...inputStyle,
                minWidth: '150px'
              }}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Data Fim:</label>
            <input
              type="date"
              name="dataFim"
              value={filtros.dataFim}
              onChange={handleInputChange}
              style={{
                ...inputStyle,
                minWidth: '150px'
              }}
            />
          </div>

          <div style={{ marginTop: '8px',display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={gerarRelatorio}
              disabled={loading}
              style={{
                ...buttonPrimaryStyle,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              <Search size={16} />
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>

            <button
              onClick={gerarPrevisao}
              disabled={loadingPrevisao}
              style={{
                ...buttonSuccessStyle,
                opacity: loadingPrevisao ? 0.7 : 1,
                cursor: loadingPrevisao ? 'not-allowed' : 'pointer'
              }}
            >
              <TrendingUp size={16} />
              {loadingPrevisao ? 'Gerando...' : 'Previsão Próximo Mês'}
            </button>

            {relatorio && (
              <button
                onClick={exportarRelatorio}
                style={buttonSuccessStyle}
              >
                <Download size={16} />
                Exportar PDF
              </button>
            )}

            {previsao && (
              <button
                onClick={exportarRelatorio}
                style={{
                  ...buttonSuccessStyle,
                  backgroundColor: '#17a2b8'
                }}
              >
                <Download size={16} />
                Exportar Previsão PDF
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={errorStyle}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {errorPrevisao && (
          <div style={errorStyle}>
            <AlertCircle size={16} />
            {errorPrevisao}
          </div>
        )}
      </div>

      {/* Seção de Previsão */}
      {previsao && (
        <>
          {/* Resumo da Previsão */}
          <div style={statsGridStyle}>
            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {previsao.resumo.quantidadeTotalPrevista}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Unidades Previstas
                  </p>
                </div>
                <TrendingUp size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>

            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #fd79a8 0%, #fdcb6e 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {previsao.resumo.totalMedicamentos}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Medicamentos Diferentes
                  </p>
                </div>
                <Package size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>

            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #00cec9 0%, #55efc4 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {previsao.resumo.totalPacientesAtivos}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Pacientes Ativos
                  </p>
                </div>
                <Users size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>
          </div>

          {/* Top 10 Medicamentos */}
          <div style={cardStyle}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#495057', marginBottom: '20px' }}>
              <Package size={20} />
              Top 10 Medicamentos - Previsão Próximo Mês
            </h3>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Medicamento</th>
                    <th style={thStyle}>Princípio Ativo</th>
                    <th style={thStyle}>Categoria</th>
                    <th style={{...thStyle, textAlign: 'center'}}>Quantidade Prevista</th>
                    <th style={{...thStyle, textAlign: 'center'}}>Pacientes Ativos</th>
                  </tr>
                </thead>
                <tbody>
                  {previsao.top10Medicamentos.map((medicamento, index) => (
                    <tr key={index}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>{medicamento.nome}</td>
                      <td style={tdStyle}>{medicamento.principioAtivo}</td>
                      <td style={tdStyle}>{medicamento.categoria}</td>
                      <td style={{
                        ...tdStyle,
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#e74c3c'
                      }}>
                        {medicamento.quantidadePrevista}
                      </td>
                      <td style={{...tdStyle, textAlign: 'center'}}>
                        {medicamento.totalPacientes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Medicamentos por Categoria */}
          {previsao.categorias.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#495057', marginBottom: '20px' }}>
                <BarChart3 size={20} />
                Previsão por Categoria
              </h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Categoria</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Quantidade Total</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Nº Medicamentos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previsao.categorias.slice(0, 8).map((categoria, index) => (
                      <tr key={index}>
                        <td style={{...tdStyle, fontWeight: 'bold'}}>{categoria.categoria}</td>
                        <td style={{
                          ...tdStyle,
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: '#0066cc'
                        }}>
                          {categoria.quantidade}
                        </td>
                        <td style={{...tdStyle, textAlign: 'center'}}>
                          {categoria.medicamentos}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Resultados do Relatório */}
      {relatorio && (
        <>
          {/* Cards de Resumo */}
          <div style={statsGridStyle}>
            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {relatorio.resumo.totalMedicamentosConsumidos}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Medicamentos Consumidos
                  </p>
                </div>
                <Package size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>

            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {relatorio.resumo.totalPacientesAtendidos}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Pacientes Atendidos
                  </p>
                </div>
                <Users size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>

            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '32px', margin: '0 0 5px 0' }}>
                    {relatorio.resumo.totalMedicamentosDiferentes}
                  </h3>
                  <p style={{ margin: '0', opacity: 0.9 }}>
                    Tipos de Medicamentos
                  </p>
                </div>
                <TrendingUp size={40} style={{ opacity: 0.8 }} />
              </div>
            </div>
          </div>

          {/* Consumo por Medicamento */}
          <div style={cardStyle}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#495057', marginBottom: '20px' }}>
              <Package size={20} />
              Consumo por Medicamento
            </h3>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Medicamento</th>
                    <th style={thStyle}>Princípio Ativo</th>
                    <th style={thStyle}>Categoria</th>
                    <th style={{...thStyle, textAlign: 'center'}}>Total Consumido</th>
                    <th style={{...thStyle, textAlign: 'center'}}>Pacientes</th>
                    <th style={{...thStyle, textAlign: 'center'}}>Previsão Próximo Mês</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio.consumoPorMedicamento.map((medicamento, index) => (
                    <tr key={index}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>{medicamento.nome}</td>
                      <td style={tdStyle}>{medicamento.principioAtivo}</td>
                      <td style={tdStyle}>{medicamento.categoria}</td>
                      <td style={{...tdStyle, textAlign: 'center', fontWeight: 'bold'}}>
                        {medicamento.totalConsumido}
                      </td>
                      <td style={{...tdStyle, textAlign: 'center'}}>
                        {medicamento.totalPacientes}
                      </td>
                      <td style={{
                        ...tdStyle,
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#0066cc'
                      }}>
                        {medicamento.previsaoProximoMes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Previsão para Próximo Mês */}
          {relatorio.previsaoProximoMes.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#495057', marginBottom: '20px' }}>
                <Clock size={20} />
                Previsão de Necessidades - Próximo Mês
              </h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Medicamento</th>
                      <th style={thStyle}>Categoria</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Quantidade Prevista</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Pacientes Ativos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.previsaoProximoMes.map((medicamento, index) => (
                      <tr key={index}>
                        <td style={{...tdStyle, fontWeight: 'bold'}}>{medicamento.nome}</td>
                        <td style={tdStyle}>{medicamento.categoria}</td>
                        <td style={{
                          ...tdStyle,
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: '#e74c3c'
                        }}>
                          {medicamento.previsaoMensal}
                        </td>
                        <td style={{...tdStyle, textAlign: 'center'}}>
                          {medicamento.pacientesAtivos}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Relatorios;